import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Building2, Search, Loader2, MapPin } from "lucide-react";

// Converts a directory record into the address object the booking forms use.
export const businessToAddress = (b) => ({
  business: b.trade_name, line1: b.address.line1, line2: b.address.line2 || "", city: b.address.city, postcode: b.address.postcode,
  lat: b.address.lat, lng: b.address.lng, contact_name: b.contact_name || "", contact_phone: b.contact_phone || "",
  label: `${b.trade_name}, ${b.address.line1}, ${b.address.city} ${b.address.postcode}`.trim(),
});

const authCfg = () => {
  const backup = localStorage.getItem("kc_admin_backup");
  return backup ? { headers: { Authorization: `Bearer ${backup}` } } : undefined;
};

// Business Name field: searches the saved Business Directory, then Google Maps. `value` mirrors the chosen business name.
export default function BusinessPicker({ which, onPick, onPickAddress, onPickName, value, candidates = [], placeholder }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [google, setGoogle] = useState([]);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const typing = useRef(false);

  useEffect(() => { if (!typing.current) setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) { setOpen(false); typing.current = false; } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const ensure = () => { if (!list) api.get("/businesses", authCfg()).then((r) => setList(r.data)).catch(() => setList([])); };

  const needle = q.trim().toLowerCase();
  const matches = (list || []).filter((b) => !needle || [b.trade_name, b.abn, b.address.line1, b.address.city, b.contact_name].some((v) => (v || "").toLowerCase().includes(needle))).slice(0, 6);

  useEffect(() => {
    if (!typing.current || needle.length < 3) { setGoogle([]); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get("/address/autocomplete", { params: { q: needle, mode: "business" }, ...(authCfg() || {}) }); setGoogle(data.results || []); }
      catch { setGoogle([]); } finally { setBusy(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [needle]);

  const pickDirectory = (b) => { typing.current = false; setQ(b.trade_name); setOpen(false); onPick(b); };
  const pickGoogle = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.get("/address/resolve", { params: { id: r.af_id }, ...(authCfg() || {}) });
      const name = data.business || r.line1;
      typing.current = false; setQ(name); setOpen(false);
      onPickAddress?.({ ...data, business: name });
    } catch { /* keep typed text */ } finally { setBusy(false); }
  };

  return (
    <div ref={box} className="relative" data-testid={`business-picker-${which}`}>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input value={q} onFocus={() => { ensure(); setOpen(true); }} onChange={(e) => { typing.current = true; setQ(e.target.value); setOpen(true); }}
          placeholder={placeholder || "Business name — searches your directory and Google Maps…"} className="h-9 pl-8 pr-8 text-sm" data-testid={`business-search-${which}`} />
        {busy && <Loader2 className="absolute right-2.5 top-2.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
      {open && list && (matches.length > 0 || google.length > 0 || needle.length >= 3 || candidates.length > 0) && (
        <ul className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-white shadow-lg" data-testid={`business-results-${which}`}>
          {!typing.current && candidates.length > 0 && <li className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Also at this address</li>}
          {!typing.current && candidates.map((c) => (
            <li key={c.place_id || c.business}>
              <button type="button" onClick={() => { setQ(c.business); setOpen(false); onPickName?.(c); }} data-testid={`business-candidate-${which}`}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent">
                <span className="flex items-center gap-1.5 font-semibold text-kilted-green"><Building2 className="h-3.5 w-3.5 text-kilted-gold" />{c.business}</span>
                {c.contact_phone && <span className="text-xs text-muted-foreground">{c.contact_phone}</span>}
              </button>
            </li>
          ))}
          {matches.map((b) => (
            <li key={b.id}>
              <button type="button" onClick={() => pickDirectory(b)} data-testid={`business-option-${b.id}`}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent">
                <span className="flex items-center gap-1.5 font-semibold text-kilted-green"><Building2 className="h-3.5 w-3.5 text-kilted-gold" />{b.trade_name}</span>
                <span className="text-xs text-muted-foreground">{b.address.line1}, {b.address.city}{b.contact_name ? ` · ${b.contact_name}` : ""}{b.contact_phone ? ` · ${b.contact_phone}` : ""}</span>
              </button>
            </li>
          ))}
          {google.length > 0 && <li className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">From Google Maps</li>}
          {google.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => pickGoogle(r)} data-testid={`business-google-${r.id}`}
                className="flex w-full items-start gap-1.5 px-3 py-2 text-left text-sm hover:bg-accent">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span>{r.label}</span>
              </button>
            </li>
          ))}
          {matches.length === 0 && google.length === 0 && !busy && !(candidates.length > 0 && !typing.current) && (
            <li className="px-3 py-2 text-xs text-muted-foreground">{needle.length >= 3 ? "No business found — keep typing or enter the address below" : "Type a business name, ABN or town"}</li>
          )}
        </ul>
      )}
    </div>
  );
}
